/* eslint-disable max-lines-per-function, max-statements */

import {
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';

import {
  buildPhaseService,
  type PhaseBuildExecutionState,
  type PhaseBuildTaskOption,
  type PhaseTaskSelectionSummaryEnvelope,
} from '../../services/buildPhaseService';

import {
  getPhaseTaskSelection,
  hydratePhaseTaskSelection,
  phaseTaskOptions,
  sanitizePhaseTaskSelection,
  usePhaseTaskSelection,
} from './phaseTaskSelectionStore';
import { scheduleTaskSelectionActionReveal } from './taskSelectionActionReveal';

type HydrationState = 'idle' | 'loading' | 'done';

interface SummaryRecord {
  currentRequestPending: boolean;
  currentSignature: string;
  hydration: HydrationState;
  inFlightCount: number;
  latestCompletedVersion: number | null;
  latestRequestedVersion: number | null;
  message: string | null;
  requestSerial: number;
  selectionSummaryId?: string;
  summarySignature?: string;
}

interface SummaryHookResult {
  isUpdating: boolean;
  message: string | null;
  onSelectionChange: (selectedTaskIds: string[]) => void;
  selectionSummaryId?: string;
}

interface SummaryHookOptions {
  fallbackMessage: string | null;
  projectId?: string | null;
  sourceEntryKey: string;
  state?: PhaseBuildExecutionState | null;
}

const EMPTY_RECORD: SummaryRecord = {
  currentRequestPending: false,
  currentSignature: '',
  hydration: 'idle',
  inFlightCount: 0,
  latestCompletedVersion: null,
  latestRequestedVersion: null,
  message: null,
  requestSerial: 0,
};
const records = new Map<string, SummaryRecord>();
const listeners = new Map<string, Set<() => void>>();

const scopeKey = (projectId?: string | null, phaseId?: string | null): string | null => (
  projectId && phaseId ? `${projectId}:${phaseId}` : null
);

const selectionSignature = (selectedTaskIds: string[]): string => (
  Array.from(new Set(selectedTaskIds)).sort().join('|')
);

const getRecord = (key: string): SummaryRecord => records.get(key) ?? EMPTY_RECORD;

const updateRecord = (
  key: string,
  update: (current: SummaryRecord) => SummaryRecord,
): SummaryRecord => {
  const next = update(getRecord(key));

  records.set(key, next);
  listeners.get(key)?.forEach((listener) => listener());

  return next;
};

const subscribe = (key: string | null, listener: () => void): (() => void) => {
  if (!key) return () => undefined;
  const keyListeners = listeners.get(key) ?? new Set<() => void>();

  keyListeners.add(listener);
  listeners.set(key, keyListeners);

  return () => {
    keyListeners.delete(listener);
    if (keyListeners.size === 0) listeners.delete(key);
  };
};

const newClientChangeId = (): string => {
  const randomUuid = globalThis.crypto?.randomUUID?.();

  return randomUuid ?? `selection-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const completedSummaryMatches = (
  envelope: PhaseTaskSelectionSummaryEnvelope,
  signature: string,
  requireLatestRequested: boolean,
): boolean => {
  const { summary } = envelope;

  if (!summary || summary.status !== 'completed') return false;
  if (selectionSignature(summary.selected_task_ids) !== signature) return false;
  if (summary.version !== envelope.latest_completed_version) return false;

  return !requireLatestRequested || summary.version === envelope.latest_requested_version;
};

const currentSelectionSignature = (
  projectId: string,
  phaseId: string,
  tasks: PhaseBuildTaskOption[],
): string => selectionSignature(
  sanitizePhaseTaskSelection(
    tasks,
    getPhaseTaskSelection(projectId, phaseId, tasks).selectedIds,
  ),
);

// Intentionally do not debounce or abort these requests: each checkbox event
// is an auditable version on the server, even when a newer response wins the UI.
const submitSelection = (
  key: string,
  projectId: string,
  phaseId: string,
  sourceEntryKey: string,
  tasks: PhaseBuildTaskOption[],
  selectedTaskIds: string[],
  revealAction = false,
): void => {
  const sanitizedIds = sanitizePhaseTaskSelection(tasks, selectedTaskIds);
  const signature = selectionSignature(sanitizedIds);
  const requestSerial = getRecord(key).requestSerial + 1;

  updateRecord(key, (current) => ({
    ...current,
    currentRequestPending: true,
    currentSignature: signature,
    inFlightCount: current.inFlightCount + 1,
    message: null,
    requestSerial,
    selectionSummaryId: undefined,
    summarySignature: undefined,
  }));
  if (revealAction) scheduleTaskSelectionActionReveal(sourceEntryKey);

  void buildPhaseService.saveTaskSelectionSummary(projectId, phaseId, {
    client_change_id: newClientChangeId(),
    selected_task_ids: sanitizedIds,
    source_entry_key: sourceEntryKey,
  }).then((envelope) => {
    const stillCurrent = getRecord(key).requestSerial === requestSerial
      && currentSelectionSignature(projectId, phaseId, tasks) === signature;
    const canDisplay = stillCurrent && completedSummaryMatches(envelope, signature, true);

    updateRecord(key, (current) => ({
      ...current,
      currentRequestPending: current.requestSerial === requestSerial
        ? false
        : current.currentRequestPending,
      inFlightCount: Math.max(0, current.inFlightCount - 1),
      latestCompletedVersion: envelope.latest_completed_version,
      latestRequestedVersion: envelope.latest_requested_version,
      ...(canDisplay && envelope.summary ? {
        message: envelope.summary.message,
        selectionSummaryId: envelope.summary.id,
        summarySignature: signature,
      } : {}),
    }));
    if (revealAction && stillCurrent) {
      scheduleTaskSelectionActionReveal(sourceEntryKey);
    }
  }).catch(() => {
    updateRecord(key, (current) => ({
      ...current,
      currentRequestPending: current.requestSerial === requestSerial
        ? false
        : current.currentRequestPending,
      inFlightCount: Math.max(0, current.inFlightCount - 1),
    }));
    if (revealAction && getRecord(key).requestSerial === requestSerial) {
      scheduleTaskSelectionActionReveal(sourceEntryKey);
    }
  });
};

const ensureHydrated = (
  key: string,
  projectId: string,
  phaseId: string,
  sourceEntryKey: string,
  tasks: PhaseBuildTaskOption[],
): void => {
  if (getRecord(key).hydration !== 'idle') return;
  updateRecord(key, (current) => ({ ...current, hydration: 'loading' }));
  const requestSerialAtStart = getRecord(key).requestSerial;

  void buildPhaseService.getTaskSelectionSummary(projectId, phaseId).then((envelope) => {
    const localRequestWasMade = getRecord(key).requestSerial !== requestSerialAtStart;
    const persistedIds = envelope.summary?.selected_task_ids;
    const hydratedIds = hydratePhaseTaskSelection(
      projectId,
      phaseId,
      tasks,
      localRequestWasMade ? undefined : persistedIds,
    );
    const signature = selectionSignature(hydratedIds);
    const canDisplay = !localRequestWasMade
      && completedSummaryMatches(envelope, signature, false);

    updateRecord(key, (current) => ({
      ...current,
      currentSignature: selectionSignature(
        sanitizePhaseTaskSelection(tasks, getPhaseTaskSelection(projectId, phaseId, tasks).selectedIds),
      ),
      hydration: 'done',
      latestCompletedVersion: envelope.latest_completed_version,
      latestRequestedVersion: envelope.latest_requested_version,
      ...(canDisplay && envelope.summary ? {
        message: envelope.summary.message,
        selectionSummaryId: envelope.summary.id,
        summarySignature: signature,
      } : {}),
    }));

    if (!localRequestWasMade && !canDisplay) {
      submitSelection(key, projectId, phaseId, sourceEntryKey, tasks, hydratedIds);
    }
  }).catch(() => {
    const currentIds = hydratePhaseTaskSelection(projectId, phaseId, tasks);

    updateRecord(key, (current) => ({
      ...current,
      currentSignature: selectionSignature(currentIds),
      hydration: 'done',
    }));
  });
};

export const usePhaseTaskSelectionSummary = ({
  fallbackMessage,
  projectId,
  sourceEntryKey,
  state,
}: SummaryHookOptions): SummaryHookResult => {
  const phaseId = state?.current_phase?.id ?? null;
  const tasks = useMemo(() => phaseTaskOptions(state), [state]);
  const selection = usePhaseTaskSelection(projectId, phaseId, tasks);
  const key = scopeKey(projectId, phaseId);
  const record = useSyncExternalStore(
    useCallback((listener) => subscribe(key, listener), [key]),
    useCallback(() => (key ? getRecord(key) : EMPTY_RECORD), [key]),
    () => EMPTY_RECORD,
  );

  useEffect(() => {
    if (!key || !projectId || !phaseId || tasks.length === 0) return;
    ensureHydrated(key, projectId, phaseId, sourceEntryKey, tasks);
  }, [key, phaseId, projectId, sourceEntryKey, tasks]);

  const onSelectionChange = useCallback((selectedTaskIds: string[]) => {
    if (!key || !projectId || !phaseId) return;
    submitSelection(key, projectId, phaseId, sourceEntryKey, tasks, selectedTaskIds, true);
  }, [key, phaseId, projectId, sourceEntryKey, tasks]);
  const currentSignature = selectionSignature(
    sanitizePhaseTaskSelection(tasks, selection.selectedIds),
  );
  const hasMatchingSummary = record.summarySignature === currentSignature;
  const transientMessage = record.currentRequestPending && !hasMatchingSummary
    ? 'Updating the build summary…'
    : record.hydration !== 'done' && key
      ? 'Loading the saved build summary…'
      : null;

  return {
    isUpdating: record.currentRequestPending,
    message: transientMessage ?? (hasMatchingSummary ? record.message : fallbackMessage),
    onSelectionChange,
    selectionSummaryId: hasMatchingSummary ? record.selectionSummaryId : undefined,
  };
};

export const usePhaseTaskSelectionSummaryVersion = (
  projectId: string | null | undefined,
  phaseId: string | null | undefined,
  selectedTaskIds: string[] | undefined,
): string | undefined => {
  const key = scopeKey(projectId, phaseId);
  const record = useSyncExternalStore(
    useCallback((listener) => subscribe(key, listener), [key]),
    useCallback(() => (key ? getRecord(key) : EMPTY_RECORD), [key]),
    () => EMPTY_RECORD,
  );
  const signature = selectionSignature(selectedTaskIds ?? []);

  return record.summarySignature === signature ? record.selectionSummaryId : undefined;
};

/** Test-only reset for the module-level server-summary cache. */
export const resetPhaseTaskSelectionSummaryStoreForTests = (): void => {
  records.clear();
  listeners.clear();
};
